// ST-1258 + ST-1275 -Claude- Login page — email/password (primary) + OAuth placeholders.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '@nearrme/ui-web';
import { getLoginOptions, login, loginConsumerWithCode, requestConsumerLoginCode, type LoginOptionsState } from '@nearrme/api-client';
import { getApiClient } from '../api/client';
import { setConsumerToken } from '../auth/session';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mode, setMode] = useState<'password' | 'email-code'>('password');
  const [codeRequested, setCodeRequested] = useState(false);
  const [loginOptions, setLoginOptions] = useState<LoginOptionsState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getLoginOptions(getApiClient())
      .then(setLoginOptions)
      .catch((err) => console.warn('[login options]', err));
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = mode === 'email-code'
        ? await loginConsumerWithCode(getApiClient(), email, code)
        : await login(getApiClient(), { email, password });
      if (res.data?.token) {
        setConsumerToken(res.data.token);
        navigate('/');
      } else {
        // ST-1290 -Claude- Surface backend message — no eating errors.
        setError(res.message ?? t('errors.generic'));
      }
    } catch (err: any) {
      // ST-1290 -Claude- Always log + surface
      console.error('[login failed]', err);
      const status = err?.response?.status;
      const message = err?.response?.data?.message ?? t('errors.generic');
      if (status === 409) {
        const signupParams = new URLSearchParams({
          email: email.trim(),
          source: 'consumer-required',
        });
        const suggestedName = getSuggestedName(email);
        if (suggestedName.firstName) signupParams.set('firstName', suggestedName.firstName);
        if (suggestedName.lastName) signupParams.set('lastName', suggestedName.lastName);
        navigate(`/signup?${signupParams.toString()}`);
        return;
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCode = async () => {
    setError(null);
    setLoading(true);
    try {
      await requestConsumerLoginCode(getApiClient(), email);
      setCodeRequested(true);
    } catch (err: any) {
      console.error('[request consumer login code failed]', err);
      setError(err?.response?.data?.message ?? 'Unable to send login code.');
    } finally {
      setLoading(false);
    }
  };

  const canUsePassword = loginOptions?.emailPasswordEnabled ?? true;
  const canUseEmailCode = loginOptions?.emailCodeEnabled ?? true;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 sm:py-14 animate-fade-in">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_420px] gap-8 items-stretch">
        <section className="hidden lg:flex lg:min-h-[560px] rounded-xl overflow-hidden bg-gradient-to-br from-brand-700 via-emerald-700 to-slate-950 text-white p-8 flex-col justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-emerald-100">Consumer account</p>
            <h1 className="mt-4 text-4xl font-bold tracking-tight">
              Continue where trusted shopping starts.
            </h1>
            <p className="mt-4 text-lg text-emerald-50">
              Sign in to keep your saved products, nearby store choices, and warranty-backed purchases together.
            </p>
          </div>
          <div className="grid gap-3">
            {[
              'Verified nearby stores',
              'Manufacturer warranty confidence',
              'Saved products, orders, and support history',
            ].map((item) => (
              <div key={item} className="rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-sm font-medium">
                {item}
              </div>
            ))}
          </div>
        </section>

        <Card padded={true} className="w-full lg:min-h-[560px]">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center">
            Consumer login
          </h1>
          <p className="mt-2 text-center text-sm text-slate-600 dark:text-slate-400">
            Sign in to save products, track orders, and keep warranty-backed purchases together.
          </p>

        <div className="mt-6 grid grid-cols-2 gap-2 rounded-lg bg-slate-100 p-1 dark:bg-slate-900">
          <button
            type="button"
            disabled={!canUsePassword}
            onClick={() => setMode('password')}
            className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === 'password' ? 'bg-white text-slate-950 shadow-sm dark:bg-slate-800 dark:text-slate-50' : 'text-slate-600 dark:text-slate-300 disabled:opacity-40'}`}
          >
            Password
          </button>
          <button
            type="button"
            disabled={!canUseEmailCode}
            onClick={() => setMode('email-code')}
            className={`rounded-md px-3 py-2 text-sm font-semibold ${mode === 'email-code' ? 'bg-white text-slate-950 shadow-sm dark:bg-slate-800 dark:text-slate-50' : 'text-slate-600 dark:text-slate-300 disabled:opacity-40'}`}
          >
            Email code
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('auth.email')}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          {mode === 'password' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('auth.password')}
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          )}
          {mode === 'email-code' && (
            <div className="grid gap-3">
              <Button type="button" variant="outline" fullWidth disabled={!email || loading} onClick={handleRequestCode}>
                {codeRequested ? 'Send code again' : 'Send code to email'}
              </Button>
              {codeRequested && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    Login code
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="text-sm text-red-500 bg-red-50 dark:bg-red-950 px-3 py-2 rounded-lg">
              {error}
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" fullWidth loading={loading} disabled={mode === 'email-code' && !codeRequested}>
            {mode === 'email-code' ? 'Sign in with code' : t('auth.loginButton')}
          </Button>
        </form>

        <div className="mt-4 flex items-center justify-between gap-3 text-sm">
          <Link to="/forgot-password" className="text-slate-600 dark:text-slate-300 hover:text-brand-700">
            Forgot password?
          </Link>
          <Link to="/signup" className="text-brand-700 dark:text-brand-300 hover:underline font-medium">
            {t('auth.signupTitle')}
          </Link>
        </div>
        <div className="mt-3 flex items-center justify-center gap-4 text-sm">
          <Link to="/business/signup" className="text-slate-600 dark:text-slate-300 hover:text-brand-700">
            Business signup
          </Link>
          <Link to="/business/login" className="text-slate-600 dark:text-slate-300 hover:text-brand-700">
            Business login
          </Link>
        </div>

        <div className="my-6 flex items-center gap-3 text-sm text-slate-400">
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          <span>{t('auth.or')}</span>
          <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
        </div>

        {/* ST-1252 -Claude- OAuth providers — placeholder UI for Phase 0 */}
        <div className="space-y-2">
          <Button variant="outline" fullWidth disabled title="SMS login will be enabled after India SMS provider setup">
            Continue with phone code (not enabled)
          </Button>
          <Button variant="outline" fullWidth disabled title={t('auth.comingSoon')}>
            Continue with passkey ({t('auth.comingSoon')})
          </Button>
          <Button variant="outline" fullWidth disabled title={t('auth.comingSoon')}>
            {t('auth.continueWithGoogle')} ({t('auth.comingSoon')})
          </Button>
        </div>
        </Card>
      </div>
    </div>
  );
}

function getSuggestedName(email: string): { firstName?: string; lastName?: string } {
  const normalized = email.trim().toLowerCase();
  if (normalized === 'admin@nearrme.com') return { firstName: 'Nearrme', lastName: 'Admin' };
  if (normalized === 'business@nearrme.com') return { firstName: 'Business', lastName: 'Owner' };

  const localPart = normalized.split('@')[0]?.replace(/[._-]+/g, ' ').trim();
  if (!localPart) return {};
  const words = localPart.split(/\s+/).filter(Boolean);
  return {
    firstName: capitalize(words[0]),
    lastName: words.length > 1 ? capitalize(words.slice(1).join(' ')) : undefined,
  };
}

function capitalize(value?: string): string | undefined {
  if (!value) return undefined;
  return value.charAt(0).toUpperCase() + value.slice(1);
}
